import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, ExternalLink, MousePointerClick } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const emptyLink = { title: '', url: '', description: '', category: 'general', icon: '', sort_order: 0, active: true };

const CATEGORIES = [
  { value: 'featured', label: 'Featured' },
  { value: 'offers', label: 'Current Offers' },
  { value: 'content', label: 'Content' },
  { value: 'tools', label: 'Tools' },
  { value: 'social', label: 'Connect / Social' },
  { value: 'general', label: 'General' },
];

export default function AdminLinks() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(emptyLink);
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchLinks = () => api.get('/admin/links').then(res => setLinks(res.data)).catch(console.error);
  useEffect(() => { fetchLinks(); }, []);

  const handleSave = async () => {
    try {
      if (editId) {
        await api.put(`/admin/links/${editId}`, form);
        toast.success('Link updated.');
      } else {
        await api.post('/admin/links', form);
        toast.success('Link created.');
      }
      setDialogOpen(false);
      setForm(emptyLink);
      setEditId(null);
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save.');
    }
  };

  const handleEdit = (link) => {
    setEditId(link._id);
    setForm({
      title: link.title || '', url: link.url || '', description: link.description || '',
      category: link.category || 'general', icon: link.icon || '',
      sort_order: link.sort_order || 0, active: link.active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await api.delete(`/admin/links/${id}`);
      toast.success('Link deleted.');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to delete.');
    }
  };

  const totalClicks = links.reduce((sum, l) => sum + (l.clicks || 0), 0);

  return (
    <AdminShell title="Links Page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
            {links.length} link{links.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-1.5 font-['Inter'] text-xs text-[hsl(var(--silver))]">
            <MousePointerClick className="h-3.5 w-3.5" />
            {totalClicks} total clicks
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/links"
            target="_blank"
            rel="noopener noreferrer"
            className="font-['Inter'] text-xs text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] flex items-center gap-1"
          >
            View page <ExternalLink className="h-3 w-3" />
          </a>
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setForm(emptyLink); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm">
                <Plus className="h-4 w-4 mr-2" /> Add Link
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
                  {editId ? 'Edit Link' : 'New Link'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Title</label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">URL</label>
                  <Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Description (optional)</label>
                  <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Category</label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                      <SelectTrigger className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)]">
                        {CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value} className="text-[hsl(var(--foreground))] font-['Inter'] text-sm">{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Sort Order</label>
                    <Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
                  <span className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{form.active ? 'Active' : 'Hidden'}</span>
                </div>
                <Button onClick={handleSave} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg">
                  {editId ? 'Update Link' : 'Create Link'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Title</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Category</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Clicks</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Status</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] py-8">No links yet. Add your first link.</TableCell></TableRow>
            ) : links.map(link => (
              <TableRow key={link._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                <TableCell>
                  <p className="font-['Inter'] text-sm font-medium text-[hsl(var(--foreground))]">{link.title}</p>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))] truncate max-w-xs">{link.url}</p>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-['Inter'] text-[9px] uppercase tracking-[0.1em] border-[rgba(168,181,178,0.2)] text-[hsl(var(--silver))]">
                    {link.category}
                  </Badge>
                </TableCell>
                <TableCell className="font-['Inter'] text-sm text-[hsl(var(--gold))]">{link.clicks || 0}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`font-['Inter'] text-[9px] uppercase ${link.active ? 'border-[rgba(122,158,126,0.3)] text-[hsl(var(--sage))]' : 'border-[rgba(168,181,178,0.2)] text-[hsl(var(--silver))]'}`}>
                    {link.active ? 'Active' : 'Hidden'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]" onClick={() => handleEdit(link)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-red-400" onClick={() => handleDelete(link._id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminShell>
  );
}
